import { Button } from '@components/utility/buttons';
import { IOSSwitch } from '@components/utility/switches';
import { useComponentDetailContext } from '@contentManagement/contexts';
import { usePatchManagementOverviewBannerMutation } from '@contentManagement/services/banners';
import { ManagementOverviewBanner } from '@contentManagement/types';
import CreateIcon from '@mui/icons-material/Create';
import { CircularProgress, FormControlLabel, Stack, Typography } from '@mui/material';
import { handleError, toaster } from '@utils/common';
import { useFormikContext } from 'formik';
import { ChangeEvent } from 'react';

export const ComponentDetailViewBar = ({ isFetching }: { isFetching: boolean }) => {
    //#region Hooks
    const {
        editModeState: [_, setEditMode],
    } = useComponentDetailContext();

    const [patchBanner, { isLoading }] = usePatchManagementOverviewBannerMutation();
    const formik = useFormikContext<ManagementOverviewBanner>();
    //#endregion

    //#region Handlers
    const handleSwitchClick = (_e: ChangeEvent<HTMLInputElement, Element>, checked: boolean) => {
        formik.setSubmitting(true);
        patchBanner({ is_published: checked })
            .unwrap()
            .then(() => {
                toaster.success({
                    title: `Banner ${checked ? 'published' : 'unpublished'} successfully`,
                    msg: `The overview banner has been ${checked ? 'published' : 'unpublished'} successfully.`,
                });
            })
            .catch((error) => {
                handleError('Failed to update banner status', error);
            })
            .finally(() => {
                formik.setSubmitting(false);
            });
    };
    //#endregion

    return (
        <Stack direction="row" alignItems="center" gap="20px" flexShrink={1}>
            <FormControlLabel
                className="gap-2"
                disabled={isLoading || isFetching}
                control={
                    <>
                        {isLoading ? <CircularProgress size={16} /> : null}
                        <IOSSwitch
                            size="small"
                            name="active"
                            onChange={handleSwitchClick}
                            checked={!!formik.values?.is_published}
                        />
                    </>
                }
                label={
                    <Stack direction="row" alignItems="center" gap="6px">
                        <Typography fontWeight={700} fontSize={16} letterSpacing={0}>
                            Active
                        </Typography>
                    </Stack>
                }
            />
            <Button
                variant="contained"
                fullRounded
                className="black-border w-[174px] max-w-full min-w-0 shrink"
                startIcon={<CreateIcon />}
                onClick={() => setEditMode(true)}
                disabled={isLoading || isFetching}
            >
                <Typography fontSize="15px" fontWeight={500} letterSpacing="0.46px">
                    Edit
                </Typography>
            </Button>
        </Stack>
    );
};
